// @ts-check
// Persistencia local (SOLO `localStorage`, sin red) del estado del intento
// cronometrado de los paneles de intento (diagramas/pseudocódigo/python), para
// que al refrescar la página el cronómetro siga corriendo y el intento siga
// abierto. Genérico y compartido: cada feature crea su propio almacén con un
// PREFIJO de clave distinto (los tres bloques de ejercicios son disjuntos, pero
// separar prefijos evita cualquier colisión y aísla su limpieza).
//
// Qué se guarda (por ejercicio): el `intentoId`, el `inicio` OFICIAL del
// servidor (ISO-8601, del que se deriva el cronómetro visual, ya sin depender
// de `Date.now()` del cliente) y si ya se pidió la pista. No se guarda el texto
// de la pista, la puntuación ni las salidas escritas: el tiempo/puntuación
// OFICIALES siguen siendo server-authoritative -- esto es solo la mínima
// "sesión de intento" del cliente para no perder el cronómetro al recargar.
//
// El backend NO expone un GET del intento abierto (solo `POST .../start`,
// idempotente pero que CREARÍA uno si no hubiera): por eso la restauración se
// apoya en este almacenamiento local. Si el estado guardado quedara obsoleto
// (p.ej. el intento se cerró en otra pestaña), la corrección responderá 409 y
// el panel lo traduce a un error controlado -- este módulo no valida contra el
// servidor.

/**
 * @typedef {{
 *   intentoId: string,
 *   inicio: string,
 *   pistaSolicitada: boolean,
 * }} IntentoPersistido
 */

/**
 * Devuelve `localStorage` si está disponible, o `null` (mismo criterio que
 * `editor-persistence.js#obtenerAlmacenamiento`).
 * @returns {Storage | null}
 */
function obtenerAlmacenamiento() {
  try {
    const storage = globalThis.localStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

/**
 * Crea un almacén de intentos ligado a `prefijo`. Todas las claves son
 * `${prefijo}.${ejercicioId}`.
 * @param {string} prefijo
 * @returns {{
 *   guardarIntento: (ejercicioId: string, intento: IntentoPersistido) => void,
 *   leerIntento: (ejercicioId: string) => IntentoPersistido | null,
 *   limpiarIntento: (ejercicioId: string) => void,
 *   clave: (ejercicioId: string) => string,
 * }}
 */
export function crearPersistenciaIntento(prefijo) {
  /**
   * @param {string} ejercicioId
   * @returns {string}
   */
  function clave(ejercicioId) {
    return `${prefijo}.${ejercicioId}`;
  }

  return {
    clave,

    /**
     * Guarda el estado del intento abierto para `ejercicioId`, sustituyendo
     * cualquier valor previo. Silencioso ante cualquier fallo de almacenamiento.
     * @param {string} ejercicioId
     * @param {IntentoPersistido} intento
     */
    guardarIntento(ejercicioId, intento) {
      if (!ejercicioId) return;
      const storage = obtenerAlmacenamiento();
      if (!storage) return;
      try {
        storage.setItem(clave(ejercicioId), JSON.stringify(intento));
      } catch {
        // Almacenamiento lleno/no disponible: se ignora (el cronómetro seguirá
        // siendo efímero en esta sesión, sin romper nada).
      }
    },

    /**
     * Lee el estado del intento persistido para `ejercicioId`, o `null` si no
     * hay nada guardado o el dato está corrupto/mal formado.
     * @param {string} ejercicioId
     * @returns {IntentoPersistido | null}
     */
    leerIntento(ejercicioId) {
      if (!ejercicioId) return null;
      const storage = obtenerAlmacenamiento();
      if (!storage) return null;
      let crudo;
      try {
        crudo = storage.getItem(clave(ejercicioId));
      } catch {
        return null;
      }
      if (!crudo) return null;
      let datos;
      try {
        datos = JSON.parse(crudo);
      } catch {
        return null;
      }
      if (typeof datos !== 'object' || datos === null) return null;
      const intentoId = /** @type {Record<string, unknown>} */ (datos).intentoId;
      const inicio = /** @type {Record<string, unknown>} */ (datos).inicio;
      if (typeof intentoId !== 'string' || intentoId === '') return null;
      if (typeof inicio !== 'string' || Number.isNaN(Date.parse(inicio))) return null;
      return {
        intentoId,
        inicio,
        pistaSolicitada: Boolean(/** @type {Record<string, unknown>} */ (datos).pistaSolicitada),
      };
    },

    /**
     * Elimina el estado del intento persistido para `ejercicioId` (p.ej. tras
     * cerrar el intento con la corrección). Silencioso ante cualquier fallo.
     * @param {string} ejercicioId
     */
    limpiarIntento(ejercicioId) {
      if (!ejercicioId) return;
      const storage = obtenerAlmacenamiento();
      if (!storage) return;
      try {
        storage.removeItem(clave(ejercicioId));
      } catch {
        // Ignorado a propósito.
      }
    },
  };
}
